import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import setupAxiosInterceptors from "./axiosConfig";
import { jwtDecode } from "jwt-decode";

interface AuthContextType {
  isUserLoggedIn: boolean;
  login: (token: string, userData: any) => void;
  logout: () => void;
  hasSubscription: boolean;
  isLoading: boolean;
  user: any; // You can type this better as needed
}

interface JwtPayload {
  exp: number;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImage?: string;
  // Add more fields if needed
}

// Create Auth Context
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(
    !!localStorage.getItem("authToken")
  );
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionTimer, setSessionTimer] = useState<NodeJS.Timeout | null>(null);
  const [warningTimer, setWarningTimer] = useState<NodeJS.Timeout | null>(null);
  const [warningShown, setWarningShown] = useState(false);
  const [timerSetupInProgress, setTimerSetupInProgress] = useState(false);
  
  // Modal state
  const [showSessionWarningModal, setShowSessionWarningModal] = useState(false);
  const [countdown, setCountdown] = useState(120); // 2 minutes in seconds
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);

  const [isHydrated, setIsHydrated] = useState(false);

  // Persist user on reload (optional)
  useEffect(() => {
    const savedUser = localStorage.getItem("authUser");
    const token = localStorage.getItem("authToken");
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
      startSessionTimer(); // Start session timer if user is already logged in
    }
    setIsHydrated(true);
  }, []);

  // Simulating check for subscription
  useEffect(() => {
    if (isUserLoggedIn) {
      // Simulating subscription status check
      const userSubscriptionStatus = !!localStorage.getItem(
        "stripeSubscriptionId"
      );
      setHasSubscription(userSubscriptionStatus);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [isUserLoggedIn]);

  // Handle subscription modal visibility
  useEffect(() => {
    if (isUserLoggedIn && !isLoading && !hasSubscription) {
      setShowSubscriptionModal(true);
    } else {
      setShowSubscriptionModal(false);
    }
  }, [isUserLoggedIn, isLoading, hasSubscription]);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("authToken");
      setIsUserLoggedIn(!!token);
      if (!token) {
        // Clear all timers when no token exists
        setSessionTimer(prev => {
          if (prev) clearTimeout(prev);
          return null;
        });
        setWarningTimer(prev => {
          if (prev) clearTimeout(prev);
          return null;
        });
        setCountdownTimer(prev => {
          if (prev) clearInterval(prev);
          return null;
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []); // Empty dependency array to prevent re-renders from timer changes

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (sessionTimer) {
        clearTimeout(sessionTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [sessionTimer, warningTimer, countdownTimer]);

  // Login function
  const login = (token: string, userData: User) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(userData)); // ✅ optional for reload
    setUser(userData); // ✅ set in context
    setIsUserLoggedIn(true);
    startSessionTimer(); // Start session timer on login
  };

  // Session timeout handler - only clears frontend state, backend handles actual logout
  const handleSessionTimeout = () => {
    // Clear frontend state
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setIsUserLoggedIn(false);
    setUser(null);
    setHasSubscription(false);
    
    // Clear all timers
    if (sessionTimer) {
      clearTimeout(sessionTimer);
      setSessionTimer(null);
    }
    if (warningTimer) {
      clearTimeout(warningTimer);
      setWarningTimer(null);
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    // Reset states
    setWarningShown(false);
    setShowSessionWarningModal(false);
    setCountdown(120);
    
    // Navigate to home - backend session will be expired by this time
    navigate("/");
  };

  // Handle manual login redirect
  const handleLoginRedirect = () => {
    // Close modal and reset states
    setShowSessionWarningModal(false);
    setWarningShown(false);
    setCountdown(120);
    
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    // Call logout and redirect
    logout();
    navigate("/");
  };

  // Handle cancel/dismiss modal
  const handleCancelModal = () => {
    // Close modal and reset states
    setShowSessionWarningModal(false);
    setWarningShown(false);
    setCountdown(120);
    
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    // Keep user logged in but restart the session timer
    startSessionTimer();
  };

  // Start countdown when modal is shown
  const startCountdown = () => {
    setCountdown(120); // Reset to 2 minutes
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCountdownTimer(null);
          // Auto logout when countdown reaches 0
          handleSessionTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setCountdownTimer(timer);
  };

  // Show session expiry warning toast
  const showSessionWarning = () => {
    
    // Prevent duplicate warnings
    if (warningShown) {
      return;
    }
    
    setWarningShown(true);
    
    // Show toast message with unique ID to prevent duplicates
    toast.warning("Your session will expire in 2 minutes. Please save your work.", {
      position: "top-right",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: "session-warning", // Unique ID prevents duplicate toasts
    });
  };

  // Start session timer - uses dynamic expiresAt from JWT token
  const startSessionTimer = () => {
    
    // Prevent multiple simultaneous timer setups
    if (timerSetupInProgress) {
      return;
    }
    
    setTimerSetupInProgress(true);
    
    // Clear existing timers
    if (sessionTimer) {
      clearTimeout(sessionTimer);
    }
    if (warningTimer) {
      clearTimeout(warningTimer);
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    // Reset warning flag and states
    setWarningShown(false);
    setShowSessionWarningModal(false);
    setCountdown(120);
    
    const token = localStorage.getItem("authToken");
    if (!token) {
      setTimerSetupInProgress(false);
      return;
    }
    
    try {
      // Decode JWT token to get expiry time
      const decoded = jwtDecode<JwtPayload>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiresAt - currentTime;
      
      // If token is already expired, logout immediately
      if (timeUntilExpiry <= 0) {
        setTimerSetupInProgress(false);
        handleSessionTimeout();
        return;
      }
      
      // Show warning 2 minutes before expiry or immediately if less than 2 minutes remaining
      const warningTime = Math.max(0, timeUntilExpiry - (2 * 60 * 1000));
      const warning = setTimeout(() => {
        showSessionWarning();
      }, warningTime);
      setWarningTimer(warning);
      
      // Set frontend timeout to match JWT expiry
      const timer = setTimeout(() => {
        handleSessionTimeout();
      }, timeUntilExpiry);
      setSessionTimer(timer);
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      // Fallback to logout if token is invalid
      handleSessionTimeout();
    } finally {
      setTimerSetupInProgress(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setIsUserLoggedIn(false);
    setUser(null);
    setHasSubscription(false);
    
    // Clear all timers
    if (sessionTimer) {
      clearTimeout(sessionTimer);
      setSessionTimer(null);
    }
    if (warningTimer) {
      clearTimeout(warningTimer);
      setWarningTimer(null);
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    // Reset states
    setWarningShown(false);
    setShowSessionWarningModal(false);
    setCountdown(120);
  };

  // Setup axios interceptors after logout function is defined
  useEffect(() => {
    setupAxiosInterceptors(logout);
  }, []);

  // Format countdown time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Context value
  const value = {
    isUserLoggedIn,
    hasSubscription,
    isLoading,
    login,
    logout,
    user
  };

  if (!isHydrated) return null;

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Session Warning Modal */}
      <Modal 
        show={showSessionWarningModal} 
        onHide={handleCancelModal}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Session Expiring Soon
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="pt-2">
          <div className="text-center">
            <p className="mb-3">
              Your session will expire in <strong className="text-danger">{formatTime(countdown)}</strong>
            </p>
            <p className="text-muted small mb-0">
              Please save your work and login again to continue.
            </p>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="border-0 pt-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={handleCancelModal}
            className="px-4 me-2"
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleLoginRedirect}
            className="px-4"
          >
            Login Again
          </Button>
        </Modal.Footer>
      </Modal>
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};